// Config -> Booking Settings: the minimum-hours / connecting-hours rules and
// block bookings, read the same way by every screen that needs them — the
// guest grid (which hours merge into one button, whether Reserve is
// enabled), App.jsx (block-aware selection, the checkout guard) and the admin
// grid (which cells merge). Keeping the rules here, rather than inline in
// three components, is what stops the guest and admin grids disagreeing
// about where a block starts and ends.

import {
  parseTimeSlotStartHour,
  getTimeSlotsForCourt,
  getGridRowsForDate,
  getWeekdayFromDateStr,
  formatHourLabel,
  getCourtLabel,
  isAddOnCourt,
} from './bookingUtils';
import {
  BOOKING_SETTINGS_CONFIG_KEY,
  DEFAULT_BOOKING_SETTINGS,
  MIN_MIN_BOOKING_HOURS,
  MAX_MIN_BOOKING_HOURS,
  BLOCK_BOOKING_MAX_END_HOUR,
  BOOKING_RULE_MESSAGES,
} from '../constants/bookingConst';

const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Firebase hands a list back as an object when its keys are sparse, and
 * drops an empty one entirely, so every list read here goes through this.
 * @param {*} value
 * @returns {Array}
 */
const asList = (value) => {
  if (Array.isArray(value)) return value.filter(v => v !== null && v !== undefined);
  if (value && typeof value === 'object') return Object.values(value).filter(v => v !== null && v !== undefined);
  return [];
};

/**
 * A whole number clamped into [min, max], or the fallback when it isn't a number.
 * @param {*} value
 * @param {number} min
 * @param {number} max
 * @param {number} fallback
 * @returns {number}
 */
const clampInt = (value, min, max, fallback) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

/**
 * One stored block, cleaned up: hours as integers, days/courts as number
 * lists. Returns null for a block whose range is empty or inverted, so a
 * half-edited block can never claim hours it doesn't describe.
 *
 * @param {object} block
 * @returns {{id: string, label: string, startHour: number, endHour: number, days: number[], courts: number[]}|null}
 */
export const normalizeBlockBooking = (block) => {
  if (!block || typeof block !== 'object') return null;
  const startHour = clampInt(block.startHour, 0, 23, NaN);
  const endHour = clampInt(block.endHour, 1, BLOCK_BOOKING_MAX_END_HOUR, NaN);
  if (!Number.isFinite(startHour) || !Number.isFinite(endHour) || endHour <= startHour) return null;
  return {
    id: String(block.id || `block-${startHour}-${endHour}`),
    label: typeof block.label === 'string' ? block.label : '',
    startHour,
    endHour,
    days: asList(block.days).map(Number).filter(d => Number.isInteger(d) && d >= 0 && d <= 6),
    courts: asList(block.courts).map(Number).filter(Number.isFinite),
  };
};

/**
 * The club's booking rules with every default filled in.
 *
 * @param {object} config
 * @returns {{minBookingHours: number, ruleDays: number[], ruleCourts: number[], allowStaggered: boolean,
 *   connectingSameCourtOnly: boolean, blockBookings: Array}}
 */
export const getBookingSettings = (config) => {
  const raw = config?.[BOOKING_SETTINGS_CONFIG_KEY] || {};
  return {
    minBookingHours: clampInt(
      raw.minBookingHours,
      MIN_MIN_BOOKING_HOURS,
      MAX_MIN_BOOKING_HOURS,
      DEFAULT_BOOKING_SETTINGS.minBookingHours,
    ),
    // Read as `!== false` / `=== true` so a config saved before this tab
    // existed lands on today's behaviour: staggered allowed, any court.
    // Where the minimum and connecting rules apply. Empty = every day /
    // every court, so a config saved before these existed is unchanged.
    ruleDays: asList(raw.ruleDays).map(Number).filter(d => Number.isInteger(d) && d >= 0 && d <= 6),
    ruleCourts: asList(raw.ruleCourts).map(Number).filter(Number.isFinite),
    allowStaggered: raw.allowStaggered !== false,
    connectingSameCourtOnly: raw.connectingSameCourtOnly === true,
    blockBookings: asList(raw.blockBookings).map(normalizeBlockBooking).filter(Boolean),
  };
};

/**
 * Whether the minimum-hours / connecting rules cover a booking day and court
 * (Config -> Booking Settings -> Days / Courts). Hours outside that scope are
 * ignored by the rules entirely — they neither need to meet the minimum nor
 * count toward it.
 *
 * @param {{ruleDays: number[], ruleCourts: number[]}} settings - from getBookingSettings
 * @param {string} dateStr - the booking day
 * @param {number|null} [court] - omit to ask only about the day
 * @returns {boolean}
 */
export const bookingRulesApply = (settings, dateStr, court = null) => {
  const weekday = getWeekdayFromDateStr(dateStr);
  if (settings.ruleDays.length > 0 && weekday !== null && !settings.ruleDays.includes(weekday)) return false;
  if (court !== null && settings.ruleCourts.length > 0 && !settings.ruleCourts.includes(Number(court))) return false;
  return true;
};

/**
 * "7:00 PM – 12:00 MN" — a block's hours as the grids and editor print them.
 * @param {{startHour: number, endHour: number}} block
 * @returns {string}
 */
export const describeBlockRange = (block) => (
  `${formatHourLabel(block.startHour)} – ${formatHourLabel(block.endHour % 24)}`
);

/**
 * Whether a block covers a court on a weekday. Empty days / courts mean
 * every day / every court, the same convention rate bands and time-slot
 * sets use.
 *
 * @param {object} block - normalised
 * @param {number|null} weekday
 * @param {number} court
 * @returns {boolean}
 */
const blockAppliesTo = (block, weekday, court) => {
  if (block.days.length > 0 && weekday !== null && !block.days.includes(weekday)) return false;
  if (block.courts.length > 0 && !block.courts.includes(Number(court))) return false;
  return true;
};

/**
 * The block a single cell belongs to, ignoring whether the court actually
 * opens for that hour (callers that care check court hours themselves).
 * The first matching block wins; the editor flags overlaps so that rule
 * never has to decide anything in practice.
 *
 * @param {Array} blocks - normalised blocks
 * @param {string} dateStr
 * @param {string} timeSlot
 * @param {number} court
 * @returns {object|null}
 */
const matchBlock = (blocks, dateStr, timeSlot, court) => {
  if (blocks.length === 0) return null;
  const weekday = getWeekdayFromDateStr(dateStr);
  const hour = parseTimeSlotStartHour(timeSlot);
  return blocks.find(b => blockAppliesTo(b, weekday, court) && hour >= b.startHour && hour < b.endHour) || null;
};

/**
 * Every hour of the block a cell belongs to, on that cell's own date and
 * court — what one tap on the block's button selects (or releases).
 *
 * Only hours the court really opens for that date are included, so a block
 * written as 5–10 PM on a day the club closes at 9 covers 5–9.
 *
 * @param {object} config
 * @param {string} dateStr - 'yyyy-MM-dd' of the cell (the ROW's date)
 * @param {string} timeSlot
 * @param {number} court
 * @returns {{block: object, slots: string[]}|null} null when the cell is not in a block
 */
export const getBlockSlotsForCell = (config, dateStr, timeSlot, court) => {
  const { blockBookings } = getBookingSettings(config);
  const block = matchBlock(blockBookings, dateStr, timeSlot, court);
  if (!block) return null;
  const slots = getTimeSlotsForCourt(config, dateStr, court)
    .map(slot => ({ slot, hour: parseTimeSlotStartHour(slot) }))
    .filter(({ hour }) => hour >= block.startHour && hour < block.endHour)
    .sort((a, b) => a.hour - b.hour)
    .map(({ slot }) => slot);
  if (!slots.includes(timeSlot)) return null;
  return { block, slots };
};

/**
 * Where each block sits on a drawn grid, so the grid can render it as one
 * button spanning several rows.
 *
 * A span is a run of CONSECUTIVE grid rows on one court that share a date
 * and a block. A row the court does not open for breaks the run, since that
 * cell renders CLOSED in its own right.
 *
 * @param {object} config
 * @param {Array<{slot: string, dateStr: string}>} gridRows - from getGridRowsForDate
 * @param {number[]} courts
 * @returns {{starts: Map<string, {rowSpan: number, slots: string[], dateStr: string, block: object}>, covered: Set<string>}}
 *   keyed `${rowIndex}|${court}`. `starts` holds the first row of each span;
 *   `covered` holds every OTHER row the span swallows, which the grid skips.
 */
export const buildBlockSpans = (config, gridRows, courts) => {
  const starts = new Map();
  const covered = new Set();
  const { blockBookings } = getBookingSettings(config);
  if (blockBookings.length === 0 || !Array.isArray(gridRows) || gridRows.length === 0) {
    return { starts, covered };
  }

  // Court hours per (date, court), resolved once — a grid only ever shows
  // one or two dates.
  const hoursCache = new Map();
  const courtOpens = (dateStr, court, slot) => {
    const key = `${dateStr}|${court}`;
    if (!hoursCache.has(key)) hoursCache.set(key, new Set(getTimeSlotsForCourt(config, dateStr, court)));
    return hoursCache.get(key).has(slot);
  };
  const blockAt = (rowIndex, court) => {
    const row = gridRows[rowIndex];
    if (!row || !courtOpens(row.dateStr, court, row.slot)) return null;
    return matchBlock(blockBookings, row.dateStr, row.slot, court);
  };

  courts.forEach((court) => {
    let i = 0;
    while (i < gridRows.length) {
      const block = blockAt(i, court);
      if (!block) { i += 1; continue; }
      const dateStr = gridRows[i].dateStr;
      let j = i + 1;
      while (j < gridRows.length && gridRows[j].dateStr === dateStr && blockAt(j, court)?.id === block.id) j += 1;
      const slots = gridRows.slice(i, j).map(r => r.slot);
      starts.set(`${i}|${court}`, { rowSpan: j - i, slots, dateStr, block });
      for (let k = i + 1; k < j; k += 1) covered.add(`${k}|${court}`);
      i = j;
    }
  });
  return { starts, covered };
};

/**
 * Overlapping blocks — same weekday, same court, shared hour. Only the first
 * would ever match a cell, so the editor warns rather than letting the
 * second one look configured while doing nothing.
 *
 * @param {Array} rawBlocks - as stored (normalised here)
 * @returns {Array<[string, string]>} pairs of overlapping block ids
 */
export const findBlockOverlaps = (rawBlocks) => {
  const blocks = asList(rawBlocks).map(normalizeBlockBooking).filter(Boolean);
  const intersects = (a, b) => a.length === 0 || b.length === 0 || a.some(x => b.includes(x));
  const pairs = [];
  for (let i = 0; i < blocks.length; i += 1) {
    for (let j = i + 1; j < blocks.length; j += 1) {
      const a = blocks[i];
      const b = blocks[j];
      if (a.startHour < b.endHour && b.startHour < a.endHour
        && intersects(a.days, b.days) && intersects(a.courts, b.courts)) {
        pairs.push([a.id, b.id]);
      }
    }
  }
  return pairs;
};

/**
 * Days since an arbitrary epoch for a 'yyyy-MM-dd' string, so hours on
 * different dates sit on one continuous timeline (11 PM Fri and 12 AM Sat
 * are adjacent). Parsed by field — `new Date(str)` would read it as UTC.
 * @param {string} dateStr
 * @returns {number}
 */
const dayNumber = (dateStr) => {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / 86400000);
};

/**
 * 'yyyy-MM-dd' shifted by whole days.
 * @param {string} dateStr
 * @param {number} delta
 * @returns {string}
 */
const shiftDate = (dateStr, delta) => {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const dt = new Date(y, m - 1, d + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

/**
 * "Fri, Oct 16" for the rule messages.
 * @param {string} dateStr
 * @returns {string}
 */
const formatShortDate = (dateStr) => {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DOW_SHORT[dt.getDay()]}, ${MON_SHORT[dt.getMonth()]} ${dt.getDate()}`;
};

/**
 * Fills a BOOKING_RULE_MESSAGES template.
 * @param {string} template
 * @param {Record<string, string|number>} values
 * @returns {string}
 */
const fillMessage = (template, values) => Object.entries(values).reduce(
  (text, [key, value]) => text.split(`{${key}}`).join(String(value)),
  template,
);

/**
 * Which grid an hour is drawn on. An after-midnight hour shown in the
 * previous night's grid (Config -> Pricing -> Open Past Midnight Until)
 * belongs to that night's session, not to the morning of its own date —
 * otherwise 11 PM–1 AM would read as two one-hour bookings on two days.
 *
 * @param {object} config
 * @param {string} dateStr
 * @returns {Set<string>} slots of `dateStr` drawn on the PREVIOUS date's grid
 */
const slotsShownOnPreviousGrid = (config, dateStr) => new Set(
  getGridRowsForDate(config, shiftDate(dateStr, -1))
    .filter(row => row.dayOffset === 1 && row.dateStr === dateStr)
    .map(row => row.slot),
);

/**
 * Checks a guest's selection against Config -> Booking Settings.
 *
 * Rules (the minimum is a floor — any number of hours above it is fine):
 *   - a block is all-or-nothing: every hour of it, on that court, or none
 *   - staggered ON: any hours, any courts, any dates, as long as the TOTAL
 *     court-hours picked reach `minBookingHours` (Court 1 and Court 2 at
 *     6–7 PM counts as 2)
 *   - staggered OFF, per booking DAY (the grid the hours were picked on),
 *     the day's hours across courts must connect with no gap, and:
 *     - "different courts allowed": any courts, the day's court-hours
 *       totalling at least `minBookingHours`
 *     - "same court only": every court booked has its own connected run of
 *       at least `minBookingHours` — one hour on a second court is refused
 *
 * Only hours on the days and courts the rules are scoped to are checked or
 * counted (see bookingRulesApply).
 *
 * Add-on columns (ball machines and the like) are left out of the hour
 * counts: they ride on a court hour and never extend the session.
 *
 * @param {object} config
 * @param {object} selectedDates - { [dateStr]: { [timeSlot]: number[] } }
 * @returns {{code: string, message: string, dateStr: string}|null} the first
 *   rule broken, in date order, or null when the selection is bookable
 */
export const getSelectionRuleIssue = (config, selectedDates) => {
  const settings = getBookingSettings(config);
  const cells = [];
  Object.entries(selectedDates || {}).forEach(([dateStr, slots]) => {
    Object.entries(slots || {}).forEach(([timeSlot, courts]) => {
      asList(courts).forEach(court => cells.push({ dateStr, timeSlot, court: Number(court) }));
    });
  });
  if (cells.length === 0) return null;

  // 1. Blocks are all-or-nothing. Checked first: a partial block is usually
  //    what a conflict trim left behind, and the fix is different.
  const selected = new Set(cells.map(c => `${c.dateStr}|${c.timeSlot}|${c.court}`));
  const sortedCells = [...cells].sort((a, b) => (a.dateStr === b.dateStr
    ? parseTimeSlotStartHour(a.timeSlot) - parseTimeSlotStartHour(b.timeSlot)
    : a.dateStr.localeCompare(b.dateStr)));
  for (const cell of sortedCells) {
    const info = getBlockSlotsForCell(config, cell.dateStr, cell.timeSlot, cell.court);
    if (info && info.slots.some(slot => !selected.has(`${cell.dateStr}|${slot}|${cell.court}`))) {
      return {
        code: 'partialBlock',
        dateStr: cell.dateStr,
        message: fillMessage(BOOKING_RULE_MESSAGES.partialBlock, {
          block: describeBlockRange(info.block),
          court: getCourtLabel(config, cell.court),
          date: formatShortDate(cell.dateStr),
        }),
      };
    }
  }

  // 2. Group the in-scope court hours into booking days on one hour timeline.
  const previousGridCache = new Map();
  const sessions = new Map();
  cells.forEach(({ dateStr, timeSlot, court }) => {
    if (isAddOnCourt(config, court)) return;
    if (!previousGridCache.has(dateStr)) previousGridCache.set(dateStr, slotsShownOnPreviousGrid(config, dateStr));
    const sessionDate = previousGridCache.get(dateStr).has(timeSlot) ? shiftDate(dateStr, -1) : dateStr;
    // Scoped by the booking day, so an after-midnight hour follows the night
    // it belongs to rather than the morning of its own date.
    if (!bookingRulesApply(settings, sessionDate, court)) return;
    const abs = dayNumber(dateStr) * 24 + parseTimeSlotStartHour(timeSlot);
    if (!sessions.has(sessionDate)) sessions.set(sessionDate, { hours: new Set(), byCourt: new Map() });
    const session = sessions.get(sessionDate);
    session.hours.add(abs);
    if (!session.byCourt.has(court)) session.byCourt.set(court, new Set());
    session.byCourt.get(court).add(abs);
  });
  if (sessions.size === 0) return null;

  const isContiguous = (sorted) => sorted.every((h, i) => i === 0 || h === sorted[i - 1] + 1);
  const courtHourCount = (byCourt) => [...byCourt.values()].reduce((n, set) => n + set.size, 0);
  const minHoursIssue = (have, template, extra) => {
    const more = settings.minBookingHours - have;
    return {
      code: 'minHours',
      message: fillMessage(template, {
        min: settings.minBookingHours,
        minPlural: settings.minBookingHours === 1 ? '' : 's',
        more,
        morePlural: more === 1 ? '' : 's',
        ...extra,
      }),
    };
  };
  const sessionDates = [...sessions.keys()].sort();

  // 3a. Staggered on: one total across the whole selection, counted in
  //     court-hours — the same number the Reserve button shows, less add-ons.
  if (settings.allowStaggered) {
    const total = sessionDates.reduce((sum, d) => sum + courtHourCount(sessions.get(d).byCourt), 0);
    if (total < settings.minBookingHours) {
      return { ...minHoursIssue(total, BOOKING_RULE_MESSAGES.minHoursTotal, {}), dateStr: sessionDates[0] };
    }
    return null;
  }

  // 3b. Staggered off: each booking day on its own.
  for (const sessionDate of sessionDates) {
    const { hours, byCourt } = sessions.get(sessionDate);
    const sortedHours = [...hours].sort((a, b) => a - b);
    const date = formatShortDate(sessionDate);

    if (!isContiguous(sortedHours)) {
      return { code: 'staggered', dateStr: sessionDate, message: fillMessage(BOOKING_RULE_MESSAGES.staggered, { date }) };
    }

    if (settings.connectingSameCourtOnly) {
      // Every court booked carries its own connected run of the minimum.
      for (const court of [...byCourt.keys()].sort((x, y) => x - y)) {
        const run = [...byCourt.get(court)].sort((x, y) => x - y);
        const courtLabel = getCourtLabel(config, court);
        if (!isContiguous(run)) {
          return {
            code: 'courtStaggered',
            dateStr: sessionDate,
            message: fillMessage(BOOKING_RULE_MESSAGES.courtStaggered, { court: courtLabel, date }),
          };
        }
        if (run.length < settings.minBookingHours) {
          return {
            ...minHoursIssue(run.length, BOOKING_RULE_MESSAGES.minHoursPerCourt, { court: courtLabel, date }),
            dateStr: sessionDate,
          };
        }
      }
    } else {
      // Different courts allowed: any courts, totalled for the day.
      const total = courtHourCount(byCourt);
      if (total < settings.minBookingHours) {
        return { ...minHoursIssue(total, BOOKING_RULE_MESSAGES.minHours, { date }), dateStr: sessionDate };
      }
    }
  }
  return null;
};

/**
 * What changed between two saves of the tab, as short phrases for the audit
 * trail ("Minimum hours 1 → 2", "Blocks 0 → 2").
 *
 * @param {object} beforeConfig
 * @param {object} afterConfig
 * @returns {string[]} empty when nothing on the tab changed
 */
export const describeBookingSettingsChanges = (beforeConfig, afterConfig) => {
  const before = getBookingSettings(beforeConfig);
  const after = getBookingSettings(afterConfig);
  const changes = [];
  const yesNo = (v) => (v ? 'allowed' : 'not allowed');
  if (before.minBookingHours !== after.minBookingHours) {
    changes.push(`Minimum hours ${before.minBookingHours} → ${after.minBookingHours}`);
  }
  if (JSON.stringify([before.ruleDays, before.ruleCourts]) !== JSON.stringify([after.ruleDays, after.ruleCourts])) {
    const scope = (st) => `${st.ruleDays.length ? st.ruleDays.map(d => DOW_SHORT[d]).join('/') : 'every day'}, `
      + `${st.ruleCourts.length ? `courts ${st.ruleCourts.join('/')}` : 'all courts'}`;
    changes.push(`Rules apply to ${scope(before)} → ${scope(after)}`);
  }
  if (before.allowStaggered !== after.allowStaggered) {
    changes.push(`Staggered hours ${yesNo(before.allowStaggered)} → ${yesNo(after.allowStaggered)}`);
  }
  if (before.connectingSameCourtOnly !== after.connectingSameCourtOnly) {
    const label = (v) => (v ? 'same court only' : 'any court');
    changes.push(`Connecting hours ${label(before.connectingSameCourtOnly)} → ${label(after.connectingSameCourtOnly)}`);
  }
  if (JSON.stringify(before.blockBookings) !== JSON.stringify(after.blockBookings)) {
    changes.push(before.blockBookings.length === after.blockBookings.length
      ? `Block bookings edited (${after.blockBookings.length})`
      : `Block bookings ${before.blockBookings.length} → ${after.blockBookings.length}`);
  }
  return changes;
};
